import { EXTERNAL_ID_SESSION_STORAGE_KEY } from '@cof/refi-web-sdk/dist/utils/stratum';
import { REFI_MFE_UUID } from '@cof/refi-web-sdk';
import { DebugService } from '@services/debug.service';
import { REFI_LIENHOLDER_CONST } from '../constants/app.constants';
import { RefiSession } from '@models/refi-session';
import { getIsV2Routing } from '@services/routing-v2.service.ts';

class _SessionService {
  private _pfci: boolean;
  private _session: RefiSession;
  private debug: DebugService;
  private sessionStorageKey: string;
  private sessionStorageKeyV2: string;

  constructor() {
    this.debug = new DebugService('SessionService');
    this.sessionStorageKey = REFI_LIENHOLDER_CONST.REFI_SESSION_KEY;
    this.sessionStorageKeyV2 = REFI_LIENHOLDER_CONST.REFI_SESSION_KEY_V2;
    this.refreshSession();
  }

  private refreshSession() {
    if (getIsV2Routing()) {
      const sessionData = sessionStorage.getItem(this.sessionStorageKeyV2);
      if (sessionData) {
        this._session = JSON.parse(sessionData)?.state?.currentSession;
      }
    } else {
      const sessionData = sessionStorage.getItem(this.sessionStorageKey);
      if (sessionData) {
        this._session = JSON.parse(sessionData);
      }
    }

    this._pfci =
      sessionStorage.getItem(REFI_LIENHOLDER_CONST.REFI_CI_KEY) === '1';
  }

  public get session() {
    if (getIsV2Routing()) {
      const sessionData = sessionStorage.getItem(this.sessionStorageKeyV2);
      if (sessionData) {
        this._session = JSON.parse(sessionData)?.state?.currentSession || {
          currentApp: {},
          eAppId: null,
        };
      }
    } else {
      const sessionData = sessionStorage.getItem(this.sessionStorageKey);
      if (sessionData) {
        this._session = JSON.parse(sessionData);
      }
    }

    this.debug.log('getting session', this._session);
    return this._session;
  }

  public updateStatFlag(statFlag: number) {
    this.updateSession({
      ...this._session,
      currentApp: { ...this.session.currentApp, statFlag },
    });
  }

  // CWE-312: the in-memory session carries PII (borrower names, lienholder
  // name/account). Never write those fields to sessionStorage — persist only
  // the non-PII fields consumers need after a reload (eAppId, referrerId, and
  // the scalar currentApp fields). PII stays in the in-memory singleton only.
  private toPersistable(session: RefiSession): RefiSession {
    if (!session) return session;
    const { borrowerInfo, ...rest } = session;
    const persistable = { ...rest } as RefiSession;
    if (rest.currentApp) {
      const { lenderInfo, ...safeApp } = rest.currentApp;
      persistable.currentApp = safeApp;
    }
    return persistable;
  }

  public updateSession(session: RefiSession) {
    this._session = { ...this._session, ...session };
    sessionStorage.setItem(
      getIsV2Routing() ? this.sessionStorageKeyV2 : this.sessionStorageKey,
      JSON.stringify(this.toPersistable(this._session))
    );
    document.dispatchEvent(new CustomEvent('update-app-store'));
  }

  get mockSession() {
    this._session = {
      eAppId: '123645364',
      borrowerInfo: {
        firstName: 'Andy',
        lastName: 'Smith',
      },
      currentApp: {
        appId: 234,
        statFlag: 11,
        appStatusId: 405,
        validityEndDate: '2024-10-20T21:12:07.397Z',
      },
    };
    return this._session;
  }

  get pfci() {
    this.debug.log('getting pfci', this._pfci);
    return this._pfci;
  }

  getExternalId() {
    return sessionStorage.getItem(EXTERNAL_ID_SESSION_STORAGE_KEY);
  }

  setReferrerId() {
    this._session.referrerId = REFI_MFE_UUID.REFI_APPLY;
    sessionStorage.setItem(
      getIsV2Routing() ? this.sessionStorageKeyV2 : this.sessionStorageKey,
      JSON.stringify(this.toPersistable(this._session))
    );
    document.dispatchEvent(new CustomEvent('update-app-store'));
  }

  removeSession() {
    this.debug.log('removeSession');
    sessionStorage.removeItem(
      getIsV2Routing() ? this.sessionStorageKeyV2 : this.sessionStorageKey
    );
    document.dispatchEvent(new CustomEvent('update-app-store'));
  }
}

export const SessionService = new _SessionService();
